import { ChangeDetectorRef, Component, inject, OnInit } from '@angular/core';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { StrategyDetailsComponent } from './strategy-details/strategy-details.component';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { AdditionalPredicatesComponent } from './additional-predicates/additional-predicates.component';
import { GuidelinesComponent } from './guidelines/guidelines.component';
import { DetailPromptsComponent } from './detail-prompts/detail-prompts.component';

import {
  FormArray,
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { CommonModule } from '@angular/common';
import { NzFormModule } from 'ng-zorro-antd/form';
import {
  BehaviorSubject,
  finalize,
  map,
  Observable,
  of,
  Subject,
  take,
  takeUntil,
} from 'rxjs';
import {
  AdminStrategyDrawerParams,
  AdminStrategyFormValue,
  AdminStrategyItem,
  FilterState,
} from '../admin-strategy';
import { AdminStrategyService } from '../admin-strategy.service';
import { DrawerService } from '../../../../core/services/drawer.service';
import { LoaderService } from '../../../../core/services/loader.service';
import { DRAWER_IDS } from '../../../../core/constants/drawer-ids.constant';
import { CommonService } from '../../../../shared/services/common.service';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { ErrorResponse } from '../../../../core/models/error.models';

@Component({
  selector: 'app-new-strategy',
  standalone: true,
  imports: [
    NzTabsModule,
    StrategyDetailsComponent,
    NzButtonModule,
    AdditionalPredicatesComponent,
    GuidelinesComponent,
    DetailPromptsComponent,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    NzFormModule,
    NzSpinModule,
  ],
  templateUrl: './new-strategy.component.html',
  styleUrl: './new-strategy.component.scss',
})
export class NewStrategyComponent implements OnInit {
  strategyId?: number;
  isEditMode = false;
  private loaderService = inject(LoaderService);
  mainForm!: FormGroup;
  currentTabIndex = 0;

  readonly maxTitleLength = 100;
  readonly maxDescriptionLength = 500;

  private filterState = new BehaviorSubject<FilterState>({
    environments: [],
    ageLevels: [],
    functions: [],
    settingEvents: [],
    antecedents: [],
    languageLevel: [],
    behaviourConsequence: [],
    behaviourResuorceType: [],
    behaviourReplacementType: [],
    behaviourSupervisionType: [],
  });

  tableParams:
    | {
        pageSize: number;
        pageIndex: number;
        sortField: string | undefined;
        sortOrder: number | undefined;
        searchTerm: string;
        ageLevels: string[];
        environments: string[];
        antecedents: string[];
        functions: string[];
        languageLevel: string[];
      }
    | undefined;

  environments$ = this.filterState.pipe(
    map(state => {
      return state.environments ?? [];
    })
  );

  ageLevels$ = this.filterState.pipe(
    map(state => {
      return state.ageLevels ?? [];
    })
  );

  functions$ = this.filterState.pipe(
    map(state => {
      return state.functions ?? [];
    })
  );

  settingEvents$ = this.filterState.pipe(
    map(state => {
      return state.settingEvents ?? [];
    })
  );

  antecedents$ = this.filterState.pipe(
    map(state => {
      return state.antecedents ?? [];
    })
  );

  languageLevel$ = this.filterState.pipe(
    map(state => {
      return state.languageLevel ?? [];
    })
  );

  behaviourConsequence$ = this.filterState.pipe(
    map(state => {
      return state.behaviourConsequence ?? [];
    })
  );

  behaviourResuorceType$ = this.filterState.pipe(
    map(state => {
      return state.behaviourResuorceType ?? [];
    })
  );

  behaviourReplacementType$ = this.filterState.pipe(
    map(state => {
      return state.behaviourReplacementType ?? [];
    })
  );

  behaviourSupervisionType$ = this.filterState.pipe(
    map(state => {
      return state.behaviourSupervisionType ?? [];
    })
  );

  private initialValidationDone = false;

  readonly tabs = [
    { title: 'Strategy Details', key: 'details', isValid: false },
    { title: 'Additional Predicates', key: 'predicates', isValid: false },
    { title: 'Guidelines', key: 'guidelines', isValid: false },
    { title: 'Detail Prompts', key: 'prompts', isValid: false },
  ];

  loading$: Observable<boolean> = of(false);
  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private adminStrategyService: AdminStrategyService,
    private drawerService: DrawerService,
    private commonService: CommonService,
    private cdr: ChangeDetectorRef
  ) {
    this.loading$ = this.loaderService.loading$;
    this.initializeForm();
    this.drawerService
      .getParams<AdminStrategyDrawerParams>(DRAWER_IDS.CREATE_ADMIN_STRATEGY)
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(params => {
        this.strategyId = params?.strategy?.id;
        this.tableParams = params?.tableParams
          ? {
              ...params.tableParams,
              sortField: params.tableParams.sortField ?? undefined,
            }
          : undefined;

        if (this.strategyId) {
          const formValue = this.adminStrategyService.mapResponseToFormValue(
            params?.strategy ?? ({} as AdminStrategyItem)
          );
          this.isEditMode = true;
          this.patchFormWithValue(formValue);
        }
      });
  }

  private initializeForm() {
    this.mainForm = this.fb.group({
      details: this.fb.group({
        resourceType: [null, [Validators.required]],
        replacementType: [null, [Validators.required]],
        title: [
          null,
          [Validators.required, Validators.maxLength(this.maxTitleLength)],
        ],
        description: [null, [Validators.required, Validators.maxLength(4000)]],
        languageLevel: ['both', [Validators.required]],
        supervisionType: [null, [Validators.required]],
        isSettingEvent: [false],
        suitableForSchool: [false],
      }),
      predicates: this.fb.group({
        ageLevel: [[]],
        environment: [[]],
        functions: [[]],
        settingEvents: [[]],
        antecedents: [[]],
        consequences: [[]],
      }),
      guidelines: this.fb.group({
        items: this.fb.array(
          [],
          [Validators.required, Validators.minLength(1)]
        ),
      }),
      prompts: this.fb.group({
        items: this.fb.array(
          [],
          [Validators.required, Validators.minLength(1)]
        ),
      }),
    });
  }

  ngOnInit() {
    if (this.mainForm) {
      this.setupFormValidationListeners();
      this.validateInitialTabs();
    }
    this.fetchFilterData();
  }

  private patchFormWithValue(formValue: AdminStrategyFormValue): void {
    this.mainForm.patchValue({
      details: formValue.details,
      predicates: formValue.predicates,
    });

    const guidelinesArray = this.mainForm.get('guidelines.items') as FormArray;
    const promptsArray = this.mainForm.get('prompts.items') as FormArray;

    guidelinesArray.clear();
    formValue.guidelines.items.forEach(item => {
      guidelinesArray.push(this.fb.group(item));
    });

    promptsArray.clear();
    formValue.prompts.items.forEach(item => {
      promptsArray.push(this.fb.group(item));
    });

    this.mainForm.markAsPristine();
  }

  fetchFilterData(): void {
    this.loaderService.show();
    this.adminStrategyService.getLookups().subscribe({
      next: response => {
        if (response.result) {
          this.filterState.next({
            environments: response.result.behaviourEnvironment,
            ageLevels: response.result.behaviourAgeLevel,
            functions: response.result.behaviourFunctions,
            settingEvents: response.result.behaviourSettingEvents,
            antecedents: response.result.behaviourAntecedents,
            languageLevel: response.result.behaviourLanguageLevel,
            behaviourConsequence: response.result.behaviourConsequence,
            behaviourResuorceType: response.result.behaviourResuorceType,
            behaviourReplacementType: response.result.behaviourReplacementType,
            behaviourSupervisionType: response.result.behaviourSupervisionType,
          });
        }
        this.loaderService.hide();
      },
      error: ({ errors, message }: ErrorResponse) => {
        const errorMessages = errors?.map(
          err => err.detailedMessage || err.description
        );
        this.commonService.notify('error', errorMessages, message);
        this.loaderService.hide();
      },
    });
  }

  get detailsFormGroup(): FormGroup {
    return this.mainForm.get('details') as FormGroup;
  }

  get predicatesFormGroup(): FormGroup {
    return this.mainForm.get('predicates') as FormGroup;
  }

  get guidelineFormGroup(): FormGroup {
    return this.mainForm.get('guidelines') as FormGroup;
  }

  get promptsFormGroup(): FormGroup {
    return this.mainForm.get('prompts') as FormGroup;
  }

  canProceedToNextStep(): boolean {
    if (this.currentTabIndex < 0 || this.currentTabIndex >= this.tabs.length) {
      return false;
    }

    switch (this.currentTabIndex) {
      case 0:
        return this.detailsFormGroup.valid;

      case 1:
        return true;

      case 2: {
        const guidelines = this.guidelineFormGroup.get('items') as FormArray;
        return guidelines.length > 0 && guidelines.valid;
      }

      case 3: {
        const prompts = this.promptsFormGroup.get('items') as FormArray;
        return prompts.length > 0 && prompts.valid;
      }

      default:
        return false;
    }
  }

  private setupFormValidationListeners() {
    if (this.mainForm) {
      this.detailsFormGroup.statusChanges.subscribe(status => {
        this.tabs[0].isValid = status === 'VALID';
      });

      this.tabs[1].isValid = true;

      this.guidelineFormGroup.statusChanges.subscribe(status => {
        this.tabs[2].isValid =
          status === 'VALID' &&
          (this.guidelineFormGroup.get('items') as FormArray).length > 0;
      });

      this.promptsFormGroup.statusChanges.subscribe(status => {
        this.tabs[3].isValid =
          status === 'VALID' &&
          (this.promptsFormGroup.get('items') as FormArray).length > 0;
      });
    }
  }

  onNext(): void {
    if (this.isLastStep() && this.mainForm.valid) {
      this.submitForm();
    } else if (this.canProceedToNextStep()) {
      this.currentTabIndex++;
    } else {
      this.commonService.notify(
        'error',
        'Please complete all required fields.'
      );
    }
  }

  private submitForm(): void {
    const formValue = this.mainForm.value as AdminStrategyFormValue;
    const payload = this.adminStrategyService.mapFormToPayload(
      formValue,
      this.isEditMode ? this.strategyId : 0
    );

    this.loaderService.show();

    const operation$ = this.isEditMode
      ? this.adminStrategyService.updateAdminStrategy(payload)
      : this.adminStrategyService.createAdminStrategy(payload);

    operation$
      .pipe(
        finalize(() => {
          this.loaderService.hide();
          this.cdr.detectChanges();
        })
      )
      .subscribe({
        next: () => {
          const action = this.isEditMode ? 'updated' : 'created';
          this.commonService.notify(
            'success',
            `Strategy ${action} successfully!`
          );
          this.adminStrategyService.refreshTable();
          this.drawerService.closeDrawer(DRAWER_IDS.CREATE_ADMIN_STRATEGY);
        },
        error: ({ errors, message }: ErrorResponse) => {
          const errorMessages = errors?.map(
            err => err.detailedMessage || err.description
          );
          this.commonService.notify('error', errorMessages, message);
        },
      });
  }

  isLastStep(): boolean {
    return this.currentTabIndex === this.tabs.length - 1;
  }

  private validateInitialTabs() {
    this.tabs[1].isValid = true;

    this.tabs[0].isValid = this.detailsFormGroup.valid;

    const guidelinesArray = this.guidelineFormGroup.get('items') as FormArray;
    this.tabs[2].isValid = guidelinesArray.length > 0 && guidelinesArray.valid;

    const promptsArray = this.promptsFormGroup.get('items') as FormArray;
    this.tabs[3].isValid = promptsArray.length > 0 && promptsArray.valid;

    this.initialValidationDone = true;
  }

  isTabDisabled(index: number): boolean {
    if (!this.initialValidationDone) {
      return false;
    }

    if (index === this.currentTabIndex || index === 0) {
      return false;
    }

    for (let i = 0; i < index; i++) {
      if (!this.tabs[i].isValid) {
        return true;
      }
    }

    return false;
  }

  getButtonText(): string {
    if (this.isLastStep()) {
      return this.isEditMode ? 'Update Strategy' : 'Create Strategy';
    }
    return 'Next';
  }

  closeDrawer() {
    this.drawerService.closeDrawer(DRAWER_IDS.CREATE_ADMIN_STRATEGY);
    this.mainForm.reset();
  }
}
